import type {
  ChatMessage as ChatMessageType,
  ReasoningStep,
  ConversationHistoryMessage,
} from '@/types/chat';
import type React from 'react';

export const updateBlocks = (
  existingBlocks: any[] | undefined,
  content: string,
  extras?: ChatMessageType['extras'],
) => {
  let blocks = [...(existingBlocks || [])];

  // Add files block first if it doesn't exist yet
  const filesData = extras?.files;
  if (filesData?.length && !blocks.some(b => b.type === 'files')) {
    blocks.push({ type: 'files', data: filesData });
  }

  const scriptData = (extras as any)?.scriptData;
  if (scriptData && !blocks.some(b => b.type === 'code')) {
    blocks.push({ type: 'code', data: scriptData });
  }

  const curveConfig = extras?.curveConfig;
  if (curveConfig && !blocks.some(b => b.type === 'charts')) {
    blocks.push({ type: 'charts', data: curveConfig });
  }

  // Add drain detection block if it doesn't exist yet
  const drainDetectionData = (extras as any)?.drainDetection;
  if (drainDetectionData && !blocks.some(b => b.type === 'drain_detection')) {
    blocks.push({ type: 'drain_detection', data: drainDetectionData });
  }

  const hasText = content && content.trim().length > 0;
  const textIndex = blocks.findIndex(b => b.type === 'text');

  if (hasText) {
    if (textIndex === -1) {
      blocks.push({ type: 'text', data: content });
    } else {
      blocks[textIndex] = { type: 'text', data: content };
    }
  } else if (textIndex !== -1) {
    blocks = blocks.filter(b => b.type !== 'text');
  }

  return blocks;
};

export const updateReasoningSteps = (
  existingSteps: ReasoningStep[] | undefined,
  newSteps: ReasoningStep[] | undefined,
) => {
  if (!newSteps || newSteps.length === 0) return existingSteps;
  return newSteps;
};

export const extractChartMetadata = (
  messages: ChatMessageType[],
): Array<{ type: string; title: string; messageIndex: number }> => {
  const chartMetadata: Array<{
    type: string;
    title: string;
    messageIndex: number;
  }> = [];
  messages.forEach((msg, idx) => {
    const chartBlocks = msg.blocks?.filter(b => b.type === 'charts');
    if (chartBlocks && chartBlocks.length > 0) {
      chartBlocks.forEach(block => {
        const charts = Array.isArray(block.data) ? block.data : [block.data];
        charts.forEach((chart: any) => {
          chartMetadata.push({
            type: chart?.config?.chart?.type || 'unknown',
            title: chart?.config?.title?.text || 'Untitled',
            messageIndex: idx,
          });
        });
      });
    }
  });
  return chartMetadata;
};

export const formatConversationHistory = (
  messages: ChatMessageType[],
): ConversationHistoryMessage[] => {
  return messages.flatMap(msg => {
    const result: any[] = [];

    if (msg.role === 'user') {
      result.push({
        type: 'human',
        content: msg.content,
        tool_calls: [],
        tool_call_id: '',
      });
    } else if (msg.role === 'assistant') {
      // Include file information in assistant message content if files were uploaded
      let content = msg.content || '';
      const files = msg.extras?.files;
      if (files && Array.isArray(files) && files.length > 0) {
        const fileInfo = files
          .map(
            (f: any, i: number) =>
              `File ${i + 1}:\nFilename: ${f.filename}\nSize: ${
                f.size
              }\nS3 URL: ${f.s3_url}`,
          )
          .join('\n\n');
        content = `${content}\n\nUploaded files:\n${fileInfo}`;
      }

      const aiMessage: any = {
        type: 'ai',
        content: content,
        tool_calls: [],
        tool_call_id: '',
      };

      result.push(aiMessage);
    }

    return result;
  });
};

const normalizeChartConfig = (chart: any): any => {
  // Handle wrapped format with chart_type and config
  if (chart.config && typeof chart.config === 'object') {
    return {
      chart: {
        type: chart.chart_type || chart.config.chart?.type || 'line',
      },
      ...chart.config,
    };
  }

  // Handle direct Highcharts format with series
  if (chart.series) {
    return {
      ...chart,
      chart: chart.chart || { type: 'line' },
    };
  }

  // Handle nested config in alternate fields
  const possibleConfig = chart.highcharts || chart.chartConfig || chart.data;
  if (possibleConfig && typeof possibleConfig === 'object') {
    return normalizeChartConfig(possibleConfig);
  }

  return chart;
};

const hasValidChartData = (chart: any): boolean => {
  const config = chart.config || chart;

  if (!config || typeof config !== 'object') return false;

  // Check for series-based charts
  if (Array.isArray(config.series)) {
    return config.series.some((series: any) => {
      const data = series?.data;
      return (
        Array.isArray(data) &&
        data.length > 0 &&
        data.some((p: any) => p != null)
      );
    });
  }

  // Check for data-based charts
  if (Array.isArray(config.data)) {
    return config.data.length > 0 && config.data.some((p: any) => p != null);
  }

  return false;
};

export const processChartConfig = (messages: any[]): any => {
  for (const msg of messages) {
    if (typeof msg === 'object' && msg.id && Array.isArray(msg.id)) {
      const messageType = msg.id[msg.id.length - 1];
      const isToolMessage = messageType === 'ToolMessage';

      if (isToolMessage && msg.kwargs) {
        if (msg.kwargs.status === 'error') {
          continue;
        }
        try {
          const content = msg.kwargs.content;
          const toolContent =
            typeof content === 'string' ? JSON.parse(content) : content;
          const chart = toolContent?.chart;

          // Check if chart exists and has valid data
          if (chart && typeof chart === 'object' && hasValidChartData(chart)) {
            const chartConfig = {
              type: 'highchart' as const,
              config: {
                ...normalizeChartConfig(chart),
                credits: { enabled: false },
              },
            };

            return chartConfig;
          }
        } catch (e) {
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              '[CHART] Unexpected: Tool succeeded but content is not valid JSON:',
              {
                toolName: msg.kwargs.name,
                contentPreview: msg.kwargs.content?.substring?.(0, 100),
              },
            );
          }
          continue;
        }
      }
    }
  }

  return undefined;
};

export const processScriptFromToolMessage = (messages: any[]): any => {
  for (const msg of messages) {
    if (typeof msg === 'object' && msg.id && Array.isArray(msg.id)) {
      const messageType = msg.id[msg.id.length - 1];
      const isToolMessage = messageType === 'ToolMessage';

      if (isToolMessage && msg.kwargs) {
        const toolName = msg.kwargs.name;

        // FIXME: this hardcoded tool name and language "ruby" should be removed
        // we could propogate metadata about the tool from the client/server instead
        if (toolName === 'generate_ruby_script') {
          if (msg.kwargs.status === 'error') {
            continue;
          }

          try {
            const content = msg.kwargs.content;
            const toolContent =
              typeof content === 'string' ? JSON.parse(content) : content;

            if (toolContent?.script && typeof toolContent.script === 'string') {
              return {
                script: toolContent.script,
                language: 'ruby',
                description: toolContent.description || '',
                detected_software: toolContent.detected_software || '',
                query: toolContent.query || '',
              };
            }
          } catch (e) {
            if (process.env.NODE_ENV === 'development') {
              console.warn(
                '[SCRIPT] Failed to parse script from tool message:',
                {
                  toolName,
                  error: e,
                },
              );
            }
            continue;
          }
        }
      }
    }
  }

  return undefined;
};

/**
 * Process drain detection results from tool messages
 */
export const processDrainDetectionFromToolMessage = (messages: any[]): any => {
  for (const msg of messages) {
    if (typeof msg === 'object' && msg.id && Array.isArray(msg.id)) {
      const messageType = msg.id[msg.id.length - 1];
      const isToolMessage = messageType === 'ToolMessage';

      if (isToolMessage && msg.kwargs) {
        const toolName = msg.kwargs.name;

        // Check for drain detection tool responses
        if (
          toolName === 'process_video' ||
          toolName === 'get_drain_detection_details'
        ) {
          if (msg.kwargs.status === 'error') {
            continue;
          }

          try {
            const content = msg.kwargs.content;
            const toolContent =
              typeof content === 'string' ? JSON.parse(content) : content;

            // Validate that this is drain detection data with required fields
            if (
              toolContent?.detection_id &&
              toolContent?.image_captions &&
              Array.isArray(toolContent.image_captions)
            ) {
              return {
                detection_id: toolContent.detection_id,
                detection_name: toolContent.detection_name || 'Drain Detection',
                detection_type: toolContent.detection_type || 'drain',
                tenant_id: toolContent.tenant_id || '',
                file_name: toolContent.file_name || 'Unknown',
                status: toolContent.status || 'unknown',
                updated_at: toolContent.updated_at || new Date().toISOString(),
                created_by: toolContent.created_by || '',
                image_captions: toolContent.image_captions,
              };
            }
          } catch (e) {
            if (process.env.NODE_ENV === 'development') {
              console.warn(
                '[DRAIN_DETECTION] Failed to parse drain detection from tool message:',
                {
                  toolName,
                  error: e,
                },
              );
            }
            continue;
          }
        }
      }
    }
  }

  return undefined;
};

export const extractAIMessageContent = (messages: any[]): string => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];

    if (typeof msg === 'string') {
      return msg;
    }

    if (typeof msg === 'object' && msg.id && Array.isArray(msg.id)) {
      const isAIMessage = msg.id[msg.id.length - 1] === 'AIMessage';
      if (isAIMessage && msg.kwargs) {
        const content = msg.kwargs.content || '';
        const hasToolCalls = msg.kwargs.tool_calls?.length > 0;

        if (content.trim() && !hasToolCalls) {
          return content;
        }
      }
    }
  }

  return '';
};

export const extractStreamContent = (chunk: any): string => {
  // Only extract content marked as final response (goes to text block)
  if (chunk?.type === 'content_stream' && chunk.content && chunk.is_final) {
    return chunk.content;
  }
  return '';
};

export const extractStreamingReasoningStep = (
  chunk: any,
): ReasoningStep | null => {
  if (chunk?.type === 'tool_log') {
    return {
      content: chunk.content || undefined,
      tools: [],
      type: 'tool_log',
    };
  }
  return null;
};

export const extractAllReasoningSteps = (messages: any[]): ReasoningStep[] => {
  const steps: ReasoningStep[] = [];

  const toolResults = new Map<
    string,
    { status: 'completed' | 'error'; description: string }
  >();

  for (const msg of messages) {
    const msgType = msg?.id?.[msg.id.length - 1];
    if (msgType === 'ToolMessage' && msg.kwargs) {
      const toolCallId = msg.kwargs.tool_call_id;
      const status = msg.kwargs.status;
      const rawContent = msg.kwargs.content;
      const isError = status === 'error';

      let description = '';
      if (isError) {
        description = 'Failed';
      } else {
        try {
          const parsed =
            typeof rawContent === 'string'
              ? JSON.parse(rawContent)
              : rawContent;
          description = parsed?.description || '';
        } catch {
          description = '';
        }
      }

      toolResults.set(toolCallId, {
        status: isError ? 'error' : 'completed',
        description,
      });
    }
  }

  for (const msg of messages) {
    const msgType = msg?.id?.[msg.id.length - 1];
    if (msgType === 'AIMessage' && msg.kwargs) {
      const toolCalls = msg.kwargs.tool_calls || [];

      // Only extract tool info - content comes from streaming with is_reasoning flag
      // This prevents duplication between text block and reasoning steps
      if (toolCalls.length > 0) {
        const tools = toolCalls.map((toolCall: any) => {
          const callId = toolCall.id;
          const toolName = formatToolName(toolCall.name || '');
          const result = toolResults.get(callId);

          return {
            name: toolName,
            callId,
            status: result?.status || 'pending',
            description: result?.description,
          };
        });

        steps.push({
          content: undefined,
          tools: tools,
          type: 'reasoning',
        });
      }
    }

    if (typeof msg === 'object' && msg.type === 'tool_log') {
      steps.push({
        content: msg.content || undefined,
        tools: [],
        type: 'tool_log',
      });
    }
  }
  return steps;
};

export const formatToolName = (toolName: string): string => {
  return toolName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const renderTextWithDownloadLinks = (text: string) => {
  const parts = text.split(/(https:\/\/wlc360ai[^\s]*)/g);

  return parts.map((part, index) => {
    if (part.match(/^https:\/\/wlc360ai/)) {
      return { type: 'url' as const, content: part, key: index };
    }
    return { type: 'text' as const, content: part, key: index };
  });
};

/**
 * Creates a streaming handler for real-time updates during chat streaming
 */
export const createStreamingHandler = (
  streamingMessageIndex: number,
  requestId: string,
  isMountedRef: React.MutableRefObject<boolean>,
  setStreamingStatus: (status: string) => void,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageType[]>>,
) => {
  return (
    content: string,
    extras?: ChatMessageType['extras'],
    reasoningSteps?: ReasoningStep[],
    transparencySummary?: ChatMessageType['transparencySummary'],
    filesUploaded?: boolean,
  ) => {
    if (!isMountedRef.current) return;

    let newStatus = '';

    if (content && content.trim().length > 0) {
      // Final content is being generated
      newStatus = 'Generating response...';
    } else if (reasoningSteps && reasoningSteps.length > 0) {
      // Check if any tools are pending
      const hasPendingTools = reasoningSteps.some(step =>
        step.tools?.some(tool => tool.status === 'pending'),
      );

      if (hasPendingTools) {
        newStatus = 'Running tools...';
      } else {
        newStatus = 'Thinking...';
      }
    } else {
      newStatus = 'Thinking...';
    }

    setStreamingStatus(newStatus);

    // Update message with streaming content
    setMessages(prev => {
      const updated = [...prev];

      if (filesUploaded) {
        const userMsgIndex = streamingMessageIndex - 1;
        if (
          updated[userMsgIndex] &&
          typeof updated[userMsgIndex].content === 'string'
        ) {
          const userContent = updated[userMsgIndex].content as string;
          updated[userMsgIndex] = {
            ...updated[userMsgIndex],
            content: userContent.replace(
              /Uploading (.*?)\.\.\./,
              'Uploaded $1',
            ),
          };
        }
      }

      const existing = updated[streamingMessageIndex];
      const newBlocks = updateBlocks(existing?.blocks, content, extras);
      const newReasoningSteps = updateReasoningSteps(
        existing?.reasoningSteps,
        reasoningSteps,
      );
      updated[streamingMessageIndex] = {
        role: 'assistant',
        content,
        timestamp: new Date().toISOString(),
        request_id: requestId,
        blocks: newBlocks,
        reasoningSteps: newReasoningSteps,
        transparencySummary,
        extras,
      } as any;
      return updated;
    });
  };
};

export const calculateReasoningStepsHash = (
  reasoningSteps: ReasoningStep[],
  lastReasoningStepsHash: string,
) => {
  const reasoningStepsHash = JSON.stringify(
    reasoningSteps.map(s => ({
      content: s.content?.substring(0, 100),
      tools: s.tools?.map(t => ({ name: t.name, status: t.status })),
    })),
  );
  const reasoningStepsChanged = reasoningStepsHash !== lastReasoningStepsHash;

  return {
    reasoningStepsHash,
    reasoningStepsChanged,
  };
};
